import {
  KNOWN_AD_RELATIONSHIP_TYPES,
  PATH_TRAVERSAL_AD_EDGE_TYPES,
  type KnownAdRelationshipType,
} from './ad-graph-edge-classification';

export type AdEdgeSubcategory = {
  name: string;
  edgeTypes: readonly KnownAdRelationshipType[];
};

export type AdEdgeCategory = {
  categoryName: string;
  subcategories: readonly AdEdgeSubcategory[];
};

export const AD_EDGE_TAXONOMY: readonly AdEdgeCategory[] = [
  {
    categoryName: 'Active Directory',
    subcategories: [
      {
        name: 'Active Directory Structure',
        edgeTypes: [
          'Contains',
          'ContainsIdentity',
          'DCFor',
          'GPLink',
          'GPOAppliesTo',
          'CanApplyGPO',
          'PropagatesACEsTo',
          'ClaimSpecialIdentity',
          'HasSIDHistory',
          'MemberOf',
          'SameForestTrust',
        ],
      },
      {
        name: 'Lateral Movement',
        edgeTypes: [
          'AdminTo',
          'AllowedToAct',
          'AllowedToDelegate',
          'CanPSRemote',
          'CanRDP',
          'ExecuteDCOM',
          'SQLAdmin',
        ],
      },
      {
        name: 'Credential Access',
        edgeTypes: [
          'CanASREPRoast',
          'CanKerberoast',
          'CoerceToTGT',
          'DCSync',
          'DumpSMSAPassword',
          'HasSession',
          'ReadGMSAPassword',
          'ReadLAPSPassword',
          'SyncLAPSPassword',
          'HasTrustKeys',
        ],
      },
      {
        name: 'Basic Object Manipulation',
        edgeTypes: [
          'AddMember',
          'AddSelf',
          'AllExtendedRights',
          'ForceChangePassword',
          'GenericAll',
          'Owns',
          'OwnsLimitedRights',
          'GenericWrite',
          'WriteDacl',
          'WriteOwner',
          'WriteOwnerLimitedRights',
        ],
      },
      {
        name: 'Advanced Object Manipulation',
        edgeTypes: [
          'AddAllowedToAct',
          'AddKeyCredentialLink',
          'WriteAccountRestrictions',
          'WriteGPLink',
          'WriteSPN',
        ],
      },
      {
        name: 'Active Directory Certificate Services',
        edgeTypes: [
          'GoldenCert',
          'ManageCA',
          'ManageCertificates',
          'ADCSESC1',
          'ADCSESC3',
          'ADCSESC4',
          'ADCSESC6a',
          'ADCSESC6b',
          'ADCSESC9a',
          'ADCSESC9b',
          'ADCSESC10a',
          'ADCSESC10b',
          'ADCSESC13',
        ],
      },
      {
        name: 'Cross Forest Trust Abuse',
        edgeTypes: ['SpoofSIDHistory', 'AbuseTGTDelegation'],
      },
      {
        name: 'Cross Platform',
        edgeTypes: ['SyncedToADUser'],
      },
      {
        name: 'NTLM Relay',
        edgeTypes: [
          'CoerceAndRelayNTLMToSMB',
          'CoerceAndRelayNTLMToADCS',
          'CoerceAndRelayNTLMToLDAP',
          'CoerceAndRelayNTLMToLDAPS',
        ],
      },
    ],
  },
];

export const AD_EDGE_TAXONOMY_EDGE_TYPES: readonly KnownAdRelationshipType[] =
  AD_EDGE_TAXONOMY.flatMap((category) =>
    category.subcategories.flatMap((subcategory) => subcategory.edgeTypes),
  );

export const getSubcategoryForAdEdgeType = (
  edgeType: string,
): AdEdgeSubcategory | undefined =>
  AD_EDGE_TAXONOMY.flatMap((category) => category.subcategories).find(
    (subcategory) =>
      subcategory.edgeTypes.some((candidate) => candidate === edgeType),
  );

const knownEdgeTypes = new Set<string>(KNOWN_AD_RELATIONSHIP_TYPES);

export const DEFAULT_SELECTED_AD_EDGE_TYPES: readonly KnownAdRelationshipType[] =
  PATH_TRAVERSAL_AD_EDGE_TYPES;

export const sanitizeAdEdgeTypeSelection = (
  edgeTypes: readonly string[],
): KnownAdRelationshipType[] => [
  ...new Set(
    edgeTypes.filter((edgeType): edgeType is KnownAdRelationshipType =>
      knownEdgeTypes.has(edgeType),
    ),
  ),
];
